
export interface City {
  name: string;
  description: string;
  businesses: string;
  highlight: string;
  uniqueSellingPoint: string;
}

export interface Industry {
  name: string;
  singularName: string;
  description: string;
  features: string[];
  challenges: string[];
  solutions: string[];
}

export const cities: Record<string, City> = {
  "denham-springs": {
    name: "Denham Springs",
    description: "A growing city in Livingston Parish, known for its antique district and proximity to Baton Rouge.",
    businesses: "thriving small businesses and retail establishments",
    highlight: "With the Antique Village drawing visitors from across the region",
    uniqueSellingPoint: "post-flood recovery has created a renewed focus on digital presence for local businesses"
  },
  "gonzales": {
    name: "Gonzales",
    description: "The Jambalaya Capital of the World and home to Tanger Outlets, attracting shoppers from across the region.",
    businesses: "retail shops and restaurants serving both locals and visitors",
    highlight: "With Tanger Outlets attracting shoppers from across Louisiana",
    uniqueSellingPoint: "businesses need strong digital presence to capture tourist and shopper traffic"
  },
  "prairieville": {
    name: "Prairieville",
    description: "One of Louisiana's fastest-growing communities with a strong residential base and expanding commercial sector.",
    businesses: "service providers and retail establishments serving a growing population",
    highlight: "As one of the fastest-growing areas in Louisiana",
    uniqueSellingPoint: "new businesses opening regularly need professional web presence to compete"
  },
  "zachary": {
    name: "Zachary",
    description: "Known for its top-rated school system and family-friendly community with growing businesses.",
    businesses: "family-oriented businesses and professional services",
    highlight: "With its reputation for excellent schools and family living",
    uniqueSellingPoint: "businesses serving education and family needs require targeted websites"
  },
  "port-allen": {
    name: "Port Allen",
    description: "A riverside community with strong industrial ties and a growing commercial sector.",
    businesses: "industrial service providers and local establishments",
    highlight: "With its strategic location on the Mississippi River",
    uniqueSellingPoint: "businesses need websites that connect industrial clients with local services"
  },
  "walker": {
    name: "Walker",
    description: "A rapidly developing community with small-town charm and increasing commercial activity.",
    businesses: "retail and service businesses supporting the local community",
    highlight: "As Walker continues to grow while maintaining its small-town feel",
    uniqueSellingPoint: "businesses benefit from websites that emphasize community connections"
  },
  "plaquemine": {
    name: "Plaquemine",
    description: "A historic river city with strong cultural heritage and industrial presence.",
    businesses: "traditional establishments and industrial service providers",
    highlight: "With its rich history and industrial background",
    uniqueSellingPoint: "businesses need websites that honor tradition while embracing modern capabilities"
  },
  "baker": {
    name: "Baker",
    description: "A close-knit community north of Baton Rouge with a focus on local business development.",
    businesses: "community-focused small businesses and services",
    highlight: "As Baker works to strengthen its local economy",
    uniqueSellingPoint: "small businesses need affordable, professional websites to grow their customer base"
  },
  "st-gabriel": {
    name: "St. Gabriel",
    description: "A growing industrial and residential community along the Mississippi River.",
    businesses: "industrial service providers and expanding local businesses",
    highlight: "With significant industrial presence and residential growth",
    uniqueSellingPoint: "businesses need websites that serve both industrial clients and community members"
  },
  "central": {
    name: "Central",
    description: "An independent city known for its strong community focus and growing business district.",
    businesses: "locally-owned establishments and community-oriented services",
    highlight: "With its strong sense of community identity and independence",
    uniqueSellingPoint: "businesses thrive with websites that emphasize local ownership and community values"
  }
};

export const industries: Record<string, Industry> = {
  "restaurants": {
    name: "Restaurants",
    singularName: "Restaurant",
    description: "Dining establishments serving various cuisines to local residents and visitors.",
    features: ["Online menus", "Reservation systems", "Food photography", "Location maps", "Mobile ordering options"],
    challenges: ["Standing out in a competitive market", "Showcasing menu items effectively", "Managing reservations efficiently"],
    solutions: ["Custom food photography integration", "Online reservation systems", "Mobile-friendly menus", "Integration with delivery platforms"]
  },
  "cafes": {
    name: "Cafes",
    singularName: "Cafe",
    description: "Coffee shops and casual dining establishments serving beverages and light meals.",
    features: ["Visual menu displays", "Order ahead functionality", "Loyalty program integration", "Event calendar for open mic nights or special events"],
    challenges: ["Building customer loyalty", "Showcasing ambiance and specialty items", "Managing event promotions"],
    solutions: ["Custom cafe atmosphere photography", "Online ordering systems", "Event calendar integration", "Social media integration"]
  },
  "dentists": {
    name: "Dentists",
    singularName: "Dentist",
    description: "Dental practices offering preventative care, cosmetic procedures, and oral health services.",
    features: ["Online appointment booking", "Service descriptions", "New patient forms", "Insurance information", "Before/after galleries"],
    challenges: ["Building patient trust", "Explaining complex procedures", "Managing appointment scheduling"],
    solutions: ["Professional healthcare design", "Patient portal integration", "Online appointment scheduling", "HIPAA-compliant contact forms"]
  },
  "salons": {
    name: "Salons",
    singularName: "Salon",
    description: "Hair and beauty establishments offering styling, treatments, and beauty services.",
    features: ["Service menus", "Stylist profiles", "Before/after galleries", "Online booking systems", "Product showcases"],
    challenges: ["Showcasing stylist work", "Managing appointments efficiently", "Selling retail products online"],
    solutions: ["Portfolio galleries", "Appointment scheduling systems", "E-commerce integration for product sales"]
  },
  "bakeries": {
    name: "Bakeries",
    singularName: "Bakery",
    description: "Establishments specializing in baked goods, custom cakes, and bread products.",
    features: ["Product galleries", "Custom order forms", "Pricing information", "Event catering details"],
    challenges: ["Showcasing fresh products", "Managing custom orders", "Coordinating event orders"],
    solutions: ["High-quality product photography", "Custom order forms", "Event order management system"]
  },
  "law-firms": {
    name: "Law Firms",
    singularName: "Law Firm",
    description: "Legal practices offering various legal services to individuals and businesses.",
    features: ["Practice area descriptions", "Attorney profiles", "Case results", "Client testimonials", "Contact forms"],
    challenges: ["Building client trust", "Explaining complex legal services", "Converting website visitors to consultations"],
    solutions: ["Professional legal design", "Secure contact forms", "Content marketing for legal topics", "Consultation scheduling"]
  },
  "real-estate-agents": {
    name: "Real Estate Agents",
    singularName: "Real Estate Agent",
    description: "Professionals helping clients buy, sell, and rent properties in the local market.",
    features: ["Property listings", "Agent profiles", "Market information", "Mortgage calculators", "Neighborhood guides"],
    challenges: ["Showcasing properties effectively", "Generating quality leads", "Standing out from competitors"],
    solutions: ["Property listing integration", "Virtual tours", "Lead capture forms", "Market analysis tools"]
  },
  "contractors": {
    name: "Contractors",
    singularName: "Contractor",
    description: "Construction and home improvement professionals offering residential and commercial services.",
    features: ["Service descriptions", "Project galleries", "Testimonials", "Quote request forms", "Licensing information"],
    challenges: ["Building trust with potential clients", "Showcasing quality of work", "Generating qualified leads"],
    solutions: ["Project portfolio galleries", "Before/after comparisons", "Detailed service descriptions", "Quote request forms"]
  },
  "plumbers": {
    name: "Plumbers",
    singularName: "Plumber",
    description: "Skilled professionals providing residential and commercial plumbing services.",
    features: ["Emergency service information", "Service areas", "Pricing guides", "Online booking", "License information"],
    challenges: ["Being found during emergencies", "Conveying reliability", "Explaining service offerings"],
    solutions: ["Emergency contact features", "Service area maps", "Mobile-friendly design for emergency searches"]
  },
  "electricians": {
    name: "Electricians",
    singularName: "Electrician",
    description: "Licensed professionals providing electrical installation and repair services.",
    features: ["Service descriptions", "Emergency contact information", "Service areas", "License and certification details"],
    challenges: ["Building trust for home services", "Being found during electrical emergencies", "Explaining complex services"],
    solutions: ["Mobile-friendly emergency pages", "Service area maps", "Clear licensing information display"]
  },
  "hvac-services": {
    name: "HVAC Services",
    singularName: "HVAC Service",
    description: "Heating, ventilation, and air conditioning services for homes and businesses.",
    features: ["Service descriptions", "Maintenance plans", "Emergency services", "Service areas", "Online scheduling"],
    challenges: ["Seasonal service promotion", "Emergency service visibility", "Explaining maintenance benefits"],
    solutions: ["Seasonal content strategy", "Emergency contact features", "Maintenance plan details"]
  },
  "landscaping-companies": {
    name: "Landscaping Companies",
    singularName: "Landscaping Company",
    description: "Professional outdoor services providing landscape design, maintenance, and installation.",
    features: ["Service descriptions", "Project galleries", "Before/after photos", "Seasonal service information"],
    challenges: ["Showcasing landscape transformations", "Seasonal service promotion", "Conveying design expertise"],
    solutions: ["High-quality project galleries", "Seasonal service content", "Design process explanations"]
  },
  "fitness-centers": {
    name: "Fitness Centers",
    singularName: "Fitness Center",
    description: "Gyms and workout facilities offering exercise equipment, classes, and personal training.",
    features: ["Class schedules", "Membership information", "Trainer profiles", "Facility photos", "Virtual tour"],
    challenges: ["Converting visitors to members", "Showcasing facility benefits", "Managing class registrations"],
    solutions: ["Membership sign-up forms", "Class registration systems", "Virtual facility tours"]
  },
  "yoga-studios": {
    name: "Yoga Studios",
    singularName: "Yoga Studio",
    description: "Specialized fitness centers offering yoga classes, workshops, and wellness services.",
    features: ["Class schedules", "Instructor bios", "Class descriptions", "Pricing information", "Workshop registration"],
    challenges: ["Conveying studio atmosphere", "Managing class registrations", "Promoting special workshops"],
    solutions: ["Atmospheric photography", "Online class booking", "Workshop registration systems"]
  },
  "medical-clinics": {
    name: "Medical Clinics",
    singularName: "Medical Clinic",
    description: "Healthcare facilities providing various medical services and patient care.",
    features: ["Service descriptions", "Provider profiles", "Patient forms", "Insurance information", "Appointment booking"],
    challenges: ["Building patient trust", "Complying with healthcare regulations", "Managing patient information securely"],
    solutions: ["HIPAA-compliant forms", "Secure patient portals", "Professional healthcare design"]
  },
  "veterinarians": {
    name: "Veterinarians",
    singularName: "Veterinarian",
    description: "Animal healthcare providers offering medical services for pets and other animals.",
    features: ["Service descriptions", "Staff profiles", "Pet care resources", "Emergency information", "Appointment scheduling"],
    challenges: ["Conveying compassionate care", "Emergency service visibility", "Managing appointments efficiently"],
    solutions: ["Pet-friendly design", "Emergency contact features", "Online appointment scheduling"]
  },
  "auto-repair-shops": {
    name: "Auto Repair Shops",
    singularName: "Auto Repair Shop",
    description: "Mechanical service centers providing vehicle maintenance and repair services.",
    features: ["Service descriptions", "Pricing information", "Appointment scheduling", "Vehicle make/model specializations"],
    challenges: ["Building trust with customers", "Explaining technical services", "Managing service appointments"],
    solutions: ["Service explanation videos", "Online appointment scheduling", "Vehicle diagnostic forms"]
  },
  "car-dealerships": {
    name: "Car Dealerships",
    singularName: "Car Dealership",
    description: "Businesses selling new and used vehicles along with related services.",
    features: ["Inventory listings", "Financing information", "Service department details", "Trade-in valuation tools"],
    challenges: ["Showcasing vehicle inventory", "Generating quality leads", "Managing multiple departments online"],
    solutions: ["Vehicle inventory integration", "Financing calculators", "Lead capture forms", "Department-specific pages"]
  },
  "retail-stores": {
    name: "Retail Stores",
    singularName: "Retail Store",
    description: "Shops selling various consumer products directly to customers.",
    features: ["Product catalogs", "Store location information", "Hours of operation", "Special promotions", "Customer loyalty programs"],
    challenges: ["Showcasing product variety", "Driving foot traffic", "Competing with online retailers"],
    solutions: ["E-commerce integration", "Location-based marketing features", "In-store pickup options"]
  },
  "boutiques": {
    name: "Boutiques",
    singularName: "Boutique",
    description: "Specialized shops offering curated clothing, accessories, or unique items.",
    features: ["Product galleries", "Brand information", "Store ambiance photos", "New arrival announcements"],
    challenges: ["Conveying unique store experience", "Showcasing curated selections", "Building customer loyalty"],
    solutions: ["High-quality product photography", "Store atmosphere features", "Customer loyalty programs"]
  },
  "photographers": {
    name: "Photographers",
    singularName: "Photographer",
    description: "Professional image creators specializing in various types of photography.",
    features: ["Portfolio galleries", "Service descriptions", "Pricing information", "Booking forms", "Client testimonials"],
    challenges: ["Showcasing work effectively", "Explaining service packages", "Streamlining booking process"],
    solutions: ["High-quality portfolio displays", "Package comparison tools", "Online booking systems"]
  },
  "wedding-planners": {
    name: "Wedding Planners",
    singularName: "Wedding Planner",
    description: "Event specialists helping couples plan and execute their wedding celebrations.",
    features: ["Portfolio galleries", "Service packages", "Vendor networks", "Planning resources", "Testimonials"],
    challenges: ["Building trust with couples", "Showcasing previous events", "Explaining planning process"],
    solutions: ["Wedding portfolio galleries", "Planning timeline tools", "Vendor directory integration"]
  },
  "event-planners": {
    name: "Event Planners",
    singularName: "Event Planner",
    description: "Professionals organizing various events from corporate functions to social gatherings.",
    features: ["Event portfolio", "Service descriptions", "Venue partnerships", "Client testimonials", "Contact forms"],
    challenges: ["Showcasing event variety", "Explaining planning process", "Generating qualified leads"],
    solutions: ["Event gallery sliders", "Planning process explanations", "Custom quote request forms"]
  },
  "catering-services": {
    name: "Catering Services",
    singularName: "Catering Service",
    description: "Food service businesses providing meals for events and gatherings.",
    features: ["Menu options", "Event portfolio", "Pricing information", "Booking forms", "Service area details"],
    challenges: ["Showcasing food quality", "Explaining service options", "Managing event bookings"],
    solutions: ["Food photography galleries", "Menu downloadable PDFs", "Online quote request systems"]
  },
  "food-trucks": {
    name: "Food Trucks",
    singularName: "Food Truck",
    description: "Mobile food businesses serving specialty cuisine at various locations.",
    features: ["Menu information", "Location schedule", "Private event booking", "Social media integration"],
    challenges: ["Communicating changing locations", "Building customer loyalty", "Managing event bookings"],
    solutions: ["Location tracker integration", "Social media feeds", "Event booking forms"]
  },
  "accountants": {
    name: "Accountants",
    singularName: "Accountant",
    description: "Financial professionals providing tax preparation and accounting services.",
    features: ["Service descriptions", "Tax resources", "Client portals", "Team profiles", "Testimonials"],
    challenges: ["Building trust with clients", "Explaining complex services", "Secure document sharing"],
    solutions: ["Professional financial design", "Resource libraries", "Secure client portals"]
  },
  "financial-advisors": {
    name: "Financial Advisors",
    singularName: "Financial Advisor",
    description: "Investment professionals providing guidance on financial planning and wealth management.",
    features: ["Service offerings", "Advisor profiles", "Client resources", "Investment philosophy", "Contact forms"],
    challenges: ["Building client trust", "Explaining complex services", "Generating qualified leads"],
    solutions: ["Professional financial design", "Credential displays", "Educational resource libraries"]
  },
  "insurance-agencies": {
    name: "Insurance Agencies",
    singularName: "Insurance Agency",
    description: "Businesses offering various insurance products and risk management services.",
    features: ["Insurance products", "Quote forms", "Claims information", "Agent profiles", "Customer resources"],
    challenges: ["Explaining policy options", "Generating quality leads", "Building customer trust"],
    solutions: ["Policy comparison tools", "Custom quote request forms", "Educational content libraries"]
  },
  "home-builders": {
    name: "Home Builders",
    singularName: "Home Builder",
    description: "Construction companies specializing in building residential homes.",
    features: ["Project galleries", "Floor plans", "Building process", "Testimonials", "Available lots/communities"],
    challenges: ["Showcasing home quality", "Explaining building process", "Converting visitors to consultations"],
    solutions: ["Interactive floor plans", "Construction timeline tools", "Virtual home tours"]
  },
  "interior-designers": {
    name: "Interior Designers",
    singularName: "Interior Designer",
    description: "Design professionals creating functional and aesthetic interior spaces.",
    features: ["Portfolio galleries", "Service descriptions", "Design process", "Client testimonials", "Consultation booking"],
    challenges: ["Showcasing design expertise", "Explaining design process", "Converting visitors to consultations"],
    solutions: ["Before/after galleries", "Design process explanations", "3D rendering displays"]
  },
  "cleaning-services": {
    name: "Cleaning Services",
    singularName: "Cleaning Service",
    description: "Businesses providing residential and commercial cleaning and maintenance.",
    features: ["Service packages", "Pricing information", "Booking forms", "Service areas", "Testimonials"],
    challenges: ["Building trust with customers", "Explaining service differences", "Managing cleaning appointments"],
    solutions: ["Service comparison charts", "Online booking systems", "Recurring service setup"]
  },
  "pest-control-services": {
    name: "Pest Control Services",
    singularName: "Pest Control Service",
    description: "Specialized companies managing and eliminating pest problems for homes and businesses.",
    features: ["Pest solution information", "Service areas", "Treatment plans", "Free inspection offers", "Contact forms"],
    challenges: ["Addressing customer urgency", "Explaining treatment processes", "Building ongoing service relationships"],
    solutions: ["Emergency contact features", "Treatment process explanations", "Maintenance plan details"]
  },
  "nonprofits": {
    name: "Nonprofits",
    singularName: "Nonprofit",
    description: "Organizations dedicated to charitable, educational, or community service missions.",
    features: ["Mission statement", "Impact reporting", "Donation systems", "Volunteer information", "Event calendars"],
    challenges: ["Communicating mission effectively", "Encouraging donations", "Recruiting volunteers"],
    solutions: ["Donation processing integration", "Impact visualization tools", "Volunteer sign-up systems"]
  },
  "churches": {
    name: "Churches",
    singularName: "Church",
    description: "Religious organizations serving spiritual and community needs.",
    features: ["Service times", "Ministry information", "Event calendars", "Sermon archives", "Online giving"],
    challenges: ["Engaging congregation online", "Communicating various ministries", "Managing event registrations"],
    solutions: ["Service livestreaming integration", "Ministry section organization", "Event registration systems"]
  },
  "daycare-centers": {
    name: "Daycare Centers",
    singularName: "Daycare Center",
    description: "Childcare facilities providing supervision and care for children.",
    features: ["Program information", "Staff profiles", "Facility photos", "Registration forms", "Parent resources"],
    challenges: ["Building parent trust", "Explaining programs", "Managing enrollment securely"],
    solutions: ["Virtual facility tours", "Secure enrollment forms", "Parent portal integration"]
  },
  "tutoring-services": {
    name: "Tutoring Services",
    singularName: "Tutoring Service",
    description: "Educational support businesses offering subject-specific help to students.",
    features: ["Subject areas", "Tutor profiles", "Testimonials", "Scheduling systems", "Pricing information"],
    challenges: ["Demonstrating educational expertise", "Managing session bookings", "Tracking student progress"],
    solutions: ["Tutor profile features", "Online session booking", "Progress tracking portals"]
  },
  "pet-grooming-services": {
    name: "Pet Grooming Services",
    singularName: "Pet Grooming Service",
    description: "Animal care businesses specializing in cleaning and styling pets.",
    features: ["Service packages", "Groomer profiles", "Before/after galleries", "Booking systems", "Pet care tips"],
    challenges: ["Building pet owner trust", "Showcasing grooming quality", "Managing appointments efficiently"],
    solutions: ["Before/after galleries", "Online appointment booking", "Pet profile management"]
  },
  "chiropractors": {
    name: "Chiropractors",
    singularName: "Chiropractor",
    description: "Healthcare providers specializing in spinal adjustment and musculoskeletal health.",
    features: ["Treatment information", "Practitioner profiles", "Patient testimonials", "Insurance details", "Appointment booking"],
    challenges: ["Explaining treatments", "Building patient trust", "Managing appointments efficiently"],
    solutions: ["Treatment explanation videos", "Online appointment scheduling", "New patient forms"]
  },
  "spas": {
    name: "Spas",
    singularName: "Spa",
    description: "Relaxation facilities offering various wellness and beauty treatments.",
    features: ["Treatment menus", "Facility photos", "Gift certificate options", "Special packages", "Online booking"],
    challenges: ["Conveying relaxing atmosphere", "Showcasing treatment options", "Managing appointments efficiently"],
    solutions: ["Atmosphere photography", "Service menu design", "Online booking systems"]
  },
  "barbershops": {
    name: "Barbershops",
    singularName: "Barbershop",
    description: "Specialized establishments offering haircuts and grooming services primarily for men.",
    features: ["Service menus", "Barber profiles", "Gallery of styles", "Hours and location", "Online check-in"],
    challenges: ["Standing out in competitive market", "Conveying barbershop atmosphere", "Managing wait times"],
    solutions: ["Atmosphere photography", "Online check-in systems", "Style galleries"]
  }
};

// Create mappings of which industries to feature for each city
// This ensures we have 8 industries per city, with good distribution
export const cityIndustryMap: Record<string, string[]> = {
  "denham-springs": ["restaurants", "auto-repair-shops", "retail-stores", "dentists", "contractors", "real-estate-agents", "fitness-centers", "insurance-agencies"],
  "gonzales": ["cafes", "boutiques", "salons", "restaurants", "plumbers", "medical-clinics", "car-dealerships", "home-builders"],
  "prairieville": ["law-firms", "daycare-centers", "dentists", "landscaping-companies", "bakeries", "veterinarians", "cleaning-services", "yoga-studios"],
  "zachary": ["churches", "electricians", "tutoring-services", "restaurants", "pet-grooming-services", "real-estate-agents", "photographers", "insurance-agencies"],
  "port-allen": ["contractors", "hvac-services", "auto-repair-shops", "cafes", "pest-control-services", "accountants", "nonprofits", "medical-clinics"],
  "walker": ["restaurants", "salons", "dentists", "retail-stores", "churches", "barbershops", "event-planners", "plumbers"],
  "plaquemine": ["boutiques", "bakeries", "financial-advisors", "electricians", "catering-services", "veterinarians", "automotive-repair-shops", "funeral-homes"],
  "baker": ["barbers", "churches", "food-trucks", "fitness-centers", "cleaning-services", "law-firms", "landscaping-companies", "restaurants"],
  "st-gabriel": ["contractors", "catering-services", "restaurants", "hvac-services", "insurance-agencies", "medical-clinics", "wedding-planners", "interior-designers"],
  "central": ["daycare-centers", "pet-grooming-services", "boutiques", "chiropractors", "restaurants", "spas", "real-estate-agents", "home-builders"]
};
